// Browser app: finds the visitor's location, downloads orbital elements and
// cloud cover from our Worker, and predicts visible passes locally.
import { parseTle, predictPasses, skyTrack, type Pass, type Satellite, type Observer } from "./passes.ts";
import { drawCodename } from "./codename.ts";
import { rankFor } from "./ranks.ts";

interface CloudHour {
  time: string;
  cloud: number;
}

const $ = <T extends HTMLElement = HTMLElement>(sel: string) => document.querySelector(sel) as T;

// ---------- local "spotted" life list (forerunner of the account logbook) ----------
const LOG_KEY = "sh.spotted.v1";
interface Sighting {
  norad: number;
  name: string;
  time: string; // pass start, ISO
  peak: number;
}
function loadLog(): Sighting[] {
  try {
    return JSON.parse(localStorage.getItem(LOG_KEY) || "[]");
  } catch {
    return [];
  }
}
function saveLog(log: Sighting[]) {
  try {
    localStorage.setItem(LOG_KEY, JSON.stringify(log));
  } catch {
    /* storage unavailable: list lasts for this visit only */
  }
}
let log = loadLog();
const passKey = (p: { norad: number; time: string }) => `${p.norad}@${p.time}`;

function store(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {}
}
function recall(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

// ---------- membership: codename and rank ----------
let codename = recall("sh.codename") || drawCodename();
store("sh.codename", codename);

// ---------- membership account (server) ----------
interface ServerSighting {
  norad: number;
  name: string;
  passStart: string;
  peak: number;
}
interface Member {
  email: string;
  codename: string;
  memberSince: string;
  sightings: ServerSighting[];
  foundingNo: number | null;
  hiddenFromBoard: boolean;
}
let member: Member | null = null;
let turnstileSiteKey: string | null = null;

function adoptMember(m: Member) {
  member = m;
  codename = m.codename;
  log = m.sightings.map((x) => ({ norad: x.norad, name: x.name, time: x.passStart, peak: x.peak }));
}

async function api(path: string, method = "GET", data?: unknown): Promise<any> {
  const r = await fetch(path, {
    method,
    headers: data ? { "content-type": "application/json" } : undefined,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "same-origin",
  });
  const out: any = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(out.error || `Request failed (${r.status})`), { status: r.status });
  return out;
}

const caughtCount = () => new Set(log.map((s) => s.norad)).size;

// ---------- formatting ----------
const timeFmt = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });
const dayFmt = new Intl.DateTimeFormat(undefined, { weekday: "long", day: "numeric", month: "long" });
const weekdayFmt = new Intl.DateTimeFormat(undefined, { weekday: "long" });
const dateFmt = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "long" });

/** Clock time as HTML, with any AM/PM marker set small so it never wraps the numerals. */
function clock(iso: string): string {
  return timeFmt
    .formatToParts(new Date(iso))
    .map((p) => (p.type === "dayPeriod" ? `<small class="ampm">${p.value}</small>` : p.type === "literal" && /^\s+$/.test(p.value) ? "" : p.value))
    .join("");
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

function friendlyName(name: string): string {
  return escapeHtml(rawName(name));
}

function rawName(name: string): string {
  if (/^ISS\b/.test(name)) return "International Space Station";
  if (/^CSS\b/.test(name)) return "Tiangong space station";
  if (/^HST\b/.test(name)) return "Hubble Space Telescope";
  return name.replace(/\s+R\/B$/, " rocket body");
}

function cloudLabel(c: number | null): { text: string; cls: string } {
  if (c === null) return { text: "No cloud forecast yet", cls: "unknown" };
  if (c < 25) return { text: `Clear sky, ${c}% cloud`, cls: "clear" };
  if (c < 65) return { text: `Partly cloudy, ${c}% cloud`, cls: "partly" };
  return { text: `Overcast, ${c}% cloud`, cls: "cloudy" };
}

function minutes(sec: number): string {
  const m = Math.max(1, Math.round(sec / 60));
  return `${m} ${m === 1 ? "minute" : "minutes"}`;
}

function countdown(iso: string): string {
  const m = Math.round((Date.parse(iso) - Date.now()) / 60000);
  if (m <= 0) return "now";
  if (m < 60) return `in ${m} min`;
  const h = Math.floor(m / 60);
  return `in ${h} h ${m % 60} min`;
}

function nightKey(iso: string): string {
  // Group passes by the evening they belong to: shift back 12 h so that
  // 02:00 on Sunday counts as Saturday night.
  const d = new Date(Date.parse(iso) - 12 * 3600_000);
  return `${weekdayFmt.format(d)} night, ${dateFmt.format(d)}`;
}

// ---------- sky dial (polar plot of the pass) ----------
function skyDial(track: { azimuth: number; elevation: number }[], size = 120): string {
  const c = size / 2;
  const r = c - 14;
  const xy = (az: number, el: number) => {
    const rr = r * (1 - Math.max(el, 0) / 90);
    const a = (az * Math.PI) / 180;
    return [c + rr * Math.sin(a), c - rr * Math.cos(a)];
  };
  const pts = track.filter((p) => p.elevation >= -1).map((p) => xy(p.azimuth, p.elevation));
  const path = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join("");
  const [sx, sy] = pts[0] ?? [c, c];
  const [ex, ey] = pts[pts.length - 1] ?? [c, c];
  return `<svg class="dial" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" aria-hidden="true">
    <circle cx="${c}" cy="${c}" r="${r}" class="dial-horizon"/>
    <circle cx="${c}" cy="${c}" r="${(r * 2) / 3}" class="dial-ring"/>
    <circle cx="${c}" cy="${c}" r="${r / 3}" class="dial-ring"/>
    <text x="${c}" y="10" class="dial-label">N</text>
    <text x="${size - 5}" y="${c + 3.5}" class="dial-label">E</text>
    <text x="${c}" y="${size - 2}" class="dial-label">S</text>
    <text x="5" y="${c + 3.5}" class="dial-label">W</text>
    <path d="${path}" class="dial-path"/>
    <circle cx="${sx.toFixed(1)}" cy="${sy.toFixed(1)}" r="2.5" class="dial-start"/>
    <circle cx="${ex.toFixed(1)}" cy="${ey.toFixed(1)}" r="3.5" class="dial-end"/>
  </svg>`;
}

// ---------- state ----------
let sats: Satellite[] = [];
let observer: Observer | null = null;
let placeName = "";
let clouds: CloudHour[] = [];
let passes: (Pass & { cloud: number | null })[] = [];
let highOnly = true;
try {
  highOnly = localStorage.getItem("sh.highOnly") !== "0";
} catch {}

function cloudAt(iso: string): number | null {
  const t = Date.parse(iso);
  let best: CloudHour | null = null;
  let diff = Infinity;
  for (const h of clouds) {
    const d = Math.abs(Date.parse(h.time) - t);
    if (d < diff) [best, diff] = [h, d];
  }
  return best && diff <= 90 * 60_000 ? best.cloud : null;
}

function setStatus(msg: string, isError = false) {
  const el = $("#status");
  el.textContent = msg;
  el.classList.toggle("error", isError);
  el.hidden = !msg;
}

// ---------- rendering ----------
function renderCard() {
  const caught = caughtCount();
  const { current, next } = rankFor(caught);
  $("#card-name").textContent = codename;
  $("#card-rank").textContent = current.name;
  $("#card-count").textContent = String(caught);
  $("#card-sightings").textContent = String(log.length);
  $("#card-kicker").textContent = member
    ? member.foundingNo
      ? `Founding member No. ${member.foundingNo}`
      : "Member of the society"
    : "Not yet a member";
  $("#card").classList.toggle("founding", !!member?.foundingNo);
  $("#card-signed-out").hidden = !!member;
  $("#card-signed-in").hidden = !member;
  $("#account").hidden = !member;
  if (member) {
    $("#card-email").textContent = member.email;
    $("#member-since").textContent = new Date(member.memberSince).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
    ($("#on-roll") as HTMLInputElement).checked = !member.hiddenFromBoard;
  }
  const prog = $("#card-progress");
  if (!next) {
    prog.innerHTML = "The highest rank of the society.";
  } else if (caught === 0) {
    prog.innerHTML = "Catch your first satellite to be admitted as a Watcher.";
  } else {
    const span = next.at - current.at;
    const pct = Math.round(((caught - current.at) / span) * 100);
    const left = next.at - caught;
    prog.innerHTML = `<div class="bar" role="presentation"><span style="width:${pct}%"></span></div>
      ${left} more ${left === 1 ? "satellite" : "different satellites"} to become a ${next.name}.`;
  }
}

// ---------- pass list filters ----------
let clearOnly = recall("sh.clearOnly") === "1";
let search = "";

function matchesSearch(p: Pass, q: string): boolean {
  if (!q) return true;
  const hay = `${p.name} ${rawName(p.name)} ${p.norad}`.toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .every((w) => hay.includes(w));
}

function render() {
  if (!observer) return;
  $("#place").textContent = placeName || `${observer.lat.toFixed(2)}°, ${observer.lon.toFixed(2)}°`;
  renderCard();
  renderHero();
  renderList();
}

function renderHero() {
  if (!observer) return;
  const now = Date.now();
  const upcoming = passes.filter((p) => Date.parse(p.end.time) > now && (!highOnly || p.peak.elevation >= 30));
  const hero = $("#hero");
  if (!upcoming.length) {
    hero.innerHTML = `<p class="target-empty">Nothing bright crosses your sky in the next 48 hours${
      highOnly ? ". Include low passes below to see the fainter chances." : "."
    }</p>`;
    return;
  }
  {
  const next = upcoming[0];
  const sat = sats.find((s) => s.norad === next.norad)!;
  const nextCloud = cloudLabel(next.cloud);
  const startsNow = Date.parse(next.start.time) <= now;
  hero.innerHTML = `
    <div class="target-text">
      <p class="target-when">${startsNow ? "Crossing your sky right now" : `Next pass ${countdown(next.start.time)}`}</p>
      <p class="target-time">${clock(next.start.time)}</p>
      <p class="target-name">${friendlyName(next.name)}</p>
      <p class="target-how">Look <strong>${next.start.direction}</strong>. It climbs to <strong>${next.peak.elevation}°</strong> in the ${next.peak.direction}
      ${next.endsBy === "shadow" ? "and fades out as it enters Earth's shadow" : `and sets in the ${next.end.direction}`}, about ${minutes(next.durationSec)} later.</p>
      <p class="cloud ${nextCloud.cls}">${nextCloud.text}</p>
    </div>
    ${skyDial(skyTrack(sat, observer, next.start.time, next.end.time), 190)}`;
  }
}

function renderList() {
  if (!observer) return;
  const now = Date.now();
  // Keep passes from the last 3 hours so a sighting can be logged after coming back inside.
  const recent = passes.filter((p) => Date.parse(p.end.time) > now - 3 * 3600_000);
  const shown = recent.filter(
    (p) =>
      (!highOnly || p.peak.elevation >= 30) &&
      (!clearOnly || (p.cloud !== null && p.cloud < 25)) &&
      matchesSearch(p, search),
  );
  const list = $("#passes");
  $("#pass-count").textContent =
    shown.length === recent.length ? `${recent.length} passes` : `Showing ${shown.length} of ${recent.length} passes`;
  if (!shown.length) {
    const filtered = search || clearOnly || highOnly;
    list.innerHTML = `<p class="pass-empty">${
      filtered ? "No passes match these filters." : "No visible passes in the next 48 hours."
    }${filtered ? ' <button type="button" class="link-button" id="clear-filters">Show all passes</button>' : ""}</p>`;
    return;
  }

  let html = "";
  let currentNight = "";
  for (const p of shown) {
    const night = nightKey(p.start.time);
    if (night !== currentNight) {
      if (currentNight) html += "</ol>";
      html += `<h3 class="night">${night}</h3><ol class="pass-list">`;
      currentNight = night;
    }
    const s = sats.find((x) => x.norad === p.norad)!;
    const cl = cloudLabel(p.cloud);
    const key = passKey({ norad: p.norad, time: p.start.time });
    const spotted = log.some((l) => passKey({ norad: l.norad, time: l.time }) === key);
    const mins = Math.max(1, Math.round(p.durationSec / 60));
    const started = Date.parse(p.start.time) <= now;
    html += `<li class="pass${spotted ? " logged" : ""}">
      <div class="pass-time">${clock(p.start.time)}</div>
      ${skyDial(skyTrack(s, observer, p.start.time, p.end.time), 64)}
      <div class="pass-main">
        <div class="pass-name">${friendlyName(p.name)}</div>
        <div class="pass-path">From ${p.start.direction}, highest <span class="peak">${p.peak.elevation}°</span> ${p.peak.direction}, ${
          p.endsBy === "shadow" ? "fades in shadow" : `sets ${p.end.direction}`
        }. ${mins} min.</div>
        <div class="cloud ${cl.cls}">${cl.text}</div>
      </div>
      ${
        started || spotted
          ? `<button class="spot ${spotted ? "on" : ""}" data-key="${key}" aria-pressed="${spotted}">${spotted ? "Logged" : "Log sighting"}</button>`
          : `<span></span>`
      }
    </li>`;
  }
  html += "</ol>";
  // Keep the reader's place when the list refreshes every minute.
  const top = list.scrollTop;
  list.innerHTML = html;
  list.scrollTop = top;
}

function setupFilters() {
  const low = $("#show-low") as HTMLInputElement;
  const clear = $("#clear-only") as HTMLInputElement;
  const box = $("#pass-search") as HTMLInputElement;
  low.checked = !highOnly;
  clear.checked = clearOnly;
  low.addEventListener("change", () => {
    highOnly = !low.checked;
    store("sh.highOnly", highOnly ? "1" : "0");
    renderHero();
    renderList();
  });
  clear.addEventListener("change", () => {
    clearOnly = clear.checked;
    store("sh.clearOnly", clearOnly ? "1" : null);
    renderList();
  });
  let t: ReturnType<typeof setTimeout> | undefined;
  box.addEventListener("input", () => {
    clearTimeout(t);
    t = setTimeout(() => {
      search = box.value.trim();
      renderList();
      $("#passes").scrollTop = 0;
    }, 120);
  });
  $("#passes").addEventListener("click", (e) => {
    if ((e.target as HTMLElement).id !== "clear-filters") return;
    search = "";
    box.value = "";
    clearOnly = false;
    clear.checked = false;
    store("sh.clearOnly", null);
    highOnly = false;
    low.checked = true;
    store("sh.highOnly", "0");
    renderHero();
    renderList();
  });
}

async function toggleSpotted(key: string) {
  const before = rankFor(caughtCount()).current;
  const idx = log.findIndex((l) => passKey({ norad: l.norad, time: l.time }) === key);
  let change: { method: "POST" | "DELETE"; body: object } | null = null;
  if (idx >= 0) {
    const [removed] = log.splice(idx, 1);
    change = { method: "DELETE", body: { norad: removed.norad, passStart: removed.time } };
  } else {
    const p = passes.find((x) => passKey({ norad: x.norad, time: x.start.time }) === key);
    if (p) {
      log.push({ norad: p.norad, name: p.name, time: p.start.time, peak: p.peak.elevation });
      // The location lets the server confirm the satellite was visible from here; it isn't stored.
      const place = observer ? { lat: observer.lat, lon: observer.lon } : undefined;
      change = { method: "POST", body: { norad: p.norad, name: p.name, passStart: p.start.time, peak: p.peak.elevation, place } };
    }
  }
  if (member && change) {
    render(); // optimistic
    try {
      const out = await api("/api/sightings", change.method, change.body);
      adoptMember({ ...member, sightings: out.sightings });
    } catch (e: any) {
      if (e.status === 401) {
        member = null;
        log = loadLog();
        setStatus("You were signed out, so that sighting wasn't saved. Sign in again to keep your logbook.", true);
      } else {
        setStatus(e.message || "Couldn't save that sighting. Check your connection and try again.", true);
        try {
          adoptMember({ ...member!, ...(await api("/api/me")) });
        } catch {}
      }
    }
  } else {
    saveLog(log);
  }
  render();
  const after = rankFor(caughtCount()).current;
  if (after.at > before.at) {
    const card = $("#card");
    card.classList.remove("promoted");
    void card.offsetWidth; // restart the animation
    card.classList.add("promoted");
    setStatus(`Promoted to ${after.name}.`);
    setTimeout(() => setStatus(""), 5000);
  }
}

function setRedLight(on: boolean) {
  if (on) document.documentElement.dataset.light = "red";
  else delete document.documentElement.dataset.light;
  $("#redlight").setAttribute("aria-pressed", String(on));
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", on ? "#0e0202" : "#171226");
  store("sh.redlight", on ? "1" : null);
}

function updateCalendarLink() {
  if (!observer) return;
  const q = `lat=${observer.lat.toFixed(2)}&lon=${observer.lon.toFixed(2)}`;
  const https = `${location.origin}/calendar.ics?${q}`;
  const cal = $<HTMLAnchorElement>("#calendar");
  cal.href = https.replace(/^https?:/, "webcal:");
  $<HTMLAnchorElement>("#calendar-google").href =
    `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(https.replace(/^https?:/, "webcal:"))}`;
  $<HTMLAnchorElement>("#api-link").href = `/api/passes?${q}`;
}

// ---------- data loading ----------
async function compute() {
  if (!observer || !sats.length) return;
  passes = predictPasses(sats, observer, { start: new Date(), hours: 48, minElevation: 10 }).map((p) => ({
    ...p,
    cloud: cloudAt(p.peak.time),
  }));
  render();
}

async function setLocation(lat: number, lon: number, name: string) {
  observer = { lat, lon, altM: 0 };
  // Remembered so sightings can be checked against the orbit when joining.
  store("sh.place", JSON.stringify({ lat: Math.round(lat * 100) / 100, lon: Math.round(lon * 100) / 100 }));
  placeName = name;
  const u = new URL(location.href);
  u.searchParams.set("lat", lat.toFixed(2));
  u.searchParams.set("lon", lon.toFixed(2));
  history.replaceState(null, "", u);
  updateCalendarLink();
  setStatus("Checking the cloud forecast…");
  try {
    const r = await fetch(`/api/clouds?lat=${lat.toFixed(2)}&lon=${lon.toFixed(2)}`);
    clouds = r.ok ? await r.json() : [];
  } catch {
    clouds = [];
  }
  setStatus("");
  await compute();
}

// ---------- joining, signing in and the account ----------
async function loadMember() {
  try {
    const cfg = await api("/api/config");
    turnstileSiteKey = cfg.turnstileSiteKey;
  } catch {}
  try {
    adoptMember(await api("/api/me"));
  } catch {
    member = null;
  }
  const q = new URLSearchParams(location.search);
  if (member && (q.has("joined") || q.has("welcome"))) {
    setStatus(q.has("joined") ? `Welcome to the society, ${member.codename}. Your logbook is now saved to your account.` : `Signed in as ${member.codename}.`);
    q.delete("joined");
    q.delete("welcome");
    history.replaceState(null, "", `${location.pathname}${q.toString() ? "?" + q : ""}`);
    setTimeout(() => setStatus(""), 8000);
  }
}

let turnstileWidget: string | null = null;
function loadTurnstile(container: HTMLElement): Promise<void> {
  return new Promise((resolve) => {
    if (!turnstileSiteKey) return resolve();
    const w = window as any;
    const renderIt = () => {
      if (turnstileWidget === null) {
        turnstileWidget = w.turnstile.render(container, {
          sitekey: turnstileSiteKey,
          theme: "dark",
          size: "flexible",
        });
      }
      resolve();
    };
    if (w.turnstile) return renderIt();
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.onload = renderIt;
    s.onerror = () => resolve();
    document.head.appendChild(s);
  });
}

function setupAccount() {
  const dialog = $<HTMLDialogElement>("#join");
  const form = $<HTMLFormElement>("#join-form");
  const msg = $("#join-message");
  const submit = $<HTMLButtonElement>("#join-submit");
  const sent = $("#join-sent");

  $("#join-open").addEventListener("click", async () => {
    form.hidden = false;
    sent.hidden = true;
    msg.textContent = "";
    $("#join-codename").textContent = codename;
    dialog.showModal();
    await loadTurnstile($("#join-turnstile"));
  });
  $("#join-close").addEventListener("click", () => dialog.close());
  dialog.addEventListener("click", (e) => {
    if (e.target === dialog) dialog.close(); // click on the backdrop
  });

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const email = ($("#join-email") as HTMLInputElement).value.trim();
    const w = window as any;
    const turnstileToken = turnstileSiteKey && w.turnstile && turnstileWidget !== null ? w.turnstile.getResponse(turnstileWidget) : undefined;
    submit.disabled = true;
    msg.textContent = "Sending…";
    msg.classList.remove("error");
    try {
      const out = await api("/api/auth/request", "POST", { email, turnstileToken });
      form.hidden = true;
      sent.hidden = false;
      $("#join-sent-to").textContent = email;
      const dev = $<HTMLAnchorElement>("#join-dev-link");
      dev.hidden = !out.devLink;
      if (out.devLink) dev.href = out.devLink;
      msg.textContent = "";
    } catch (err: any) {
      msg.textContent = err.message;
      msg.classList.add("error");
      if (turnstileWidget !== null) w.turnstile?.reset(turnstileWidget);
    } finally {
      submit.disabled = false;
    }
  });

  $("#signout").addEventListener("click", async () => {
    try {
      await api("/api/auth/logout", "POST", {});
    } catch {}
    member = null;
    log = loadLog();
    codename = recall("sh.codename") || drawCodename();
    setStatus("Signed out. Your logbook stays safe in your account.");
    setTimeout(() => setStatus(""), 6000);
    render();
    renderCard();
  });

  $("#on-roll").addEventListener("change", async (e) => {
    const box = e.target as HTMLInputElement;
    try {
      adoptMember(await api("/api/me", "PATCH", { action: "board-visibility", hidden: !box.checked }));
      setStatus(box.checked ? "Your codename is back on the roll." : "Your codename is now hidden from the roll.");
      setTimeout(() => setStatus(""), 5000);
    } catch (err: any) {
      box.checked = !box.checked;
      setStatus(err.message, true);
    }
  });

  if (new URLSearchParams(location.search).has("join")) {
    history.replaceState(null, "", location.pathname);
    setTimeout(() => $("#join-open").click(), 0);
  }

  const del = $<HTMLButtonElement>("#delete-account");
  const delConfirm = $("#delete-confirm");
  del.addEventListener("click", () => {
    delConfirm.hidden = false;
    del.hidden = true;
    $<HTMLButtonElement>("#delete-yes").focus();
  });
  $("#delete-no").addEventListener("click", () => {
    delConfirm.hidden = true;
    del.hidden = false;
  });
  $("#delete-yes").addEventListener("click", async () => {
    try {
      await api("/api/me", "DELETE", {});
      member = null;
      saveLog([]);
      log = [];
      codename = drawCodename();
      store("sh.codename", codename);
      delConfirm.hidden = true;
      del.hidden = false;
      setStatus("Your account and everything in it has been deleted.");
      render();
      renderCard();
    } catch (e: any) {
      setStatus(e.message, true);
    }
  });
}

async function init() {
  renderCard();
  setRedLight(document.documentElement.dataset.light === "red");
  $("#redlight").addEventListener("click", () => setRedLight(document.documentElement.dataset.light !== "red"));
  $("#rename").addEventListener("click", async () => {
    if (member) {
      try {
        adoptMember(await api("/api/me", "PATCH", { action: "redraw-codename" }));
      } catch (e: any) {
        setStatus(e.message, true);
      }
    } else {
      codename = drawCodename();
      store("sh.codename", codename);
    }
    renderCard();
  });
  setupAccount();
  // Membership loads alongside the orbits rather than holding them up.
  loadMember().then(() => {
    renderCard();
    render();
  });

  setupFilters();

  $("#passes").addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("button.spot");
    if (btn?.dataset.key) toggleSpotted(btn.dataset.key);
  });

  $("#locate").addEventListener("click", () => {
    if (!navigator.geolocation) return setStatus("Your browser can't share its location.", true);
    setStatus("Finding you…");
    navigator.geolocation.getCurrentPosition(
      (pos) => setLocation(pos.coords.latitude, pos.coords.longitude, "Your location"),
      () => setStatus("Location permission was declined, so we're using an approximate position.", true),
      { maximumAge: 600_000, timeout: 15_000 },
    );
  });

  setStatus("Downloading orbits…");
  const [tleRes, where] = await Promise.all([
    fetch("/api/tle").then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status))))),
    fetch("/api/where").then((r) => r.json() as Promise<{ lat?: number | null; lon?: number | null; city?: string; country?: string }>).catch(() => ({}) as { lat?: number | null; lon?: number | null; city?: string; country?: string }),
  ]).catch((e) => {
    setStatus("Couldn't load satellite orbits right now. Please try again in a moment.", true);
    throw e;
  });
  sats = parseTle(tleRes);

  const q = new URLSearchParams(location.search);
  const qlat = Number(q.get("lat"));
  const qlon = Number(q.get("lon"));
  if (q.has("lat") && Number.isFinite(qlat) && Number.isFinite(qlon)) {
    await setLocation(qlat, qlon, "");
  } else if (where.lat != null && where.lon != null) {
    await setLocation(where.lat, where.lon, [where.city, where.country].filter(Boolean).join(", ") + " (approx.)");
  } else {
    await setLocation(58.97, 5.73, "Stavanger, NO");
  }

  // Keep countdowns fresh; recompute every 30 minutes for long-open tabs.
  setInterval(render, 60_000);
  setInterval(compute, 30 * 60_000);
}

init();
